import { useState, useEffect } from 'react';
import { X, Download, Share, ArrowUp } from 'lucide-react';
import { useTourStore } from '../../stores/tourStore';
import { usePwaInstall } from './usePwaInstall';

export function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const { isIOS, isInstallable, canShowInstall, promptInstall } = usePwaInstall();
  const tourActive = useTourStore((s) => s.isActive);

  useEffect(() => {
    if (!canShowInstall) return;

    const timer = setTimeout(() => {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed && (isInstallable || isIOS)) {
        setShowPrompt(true);
      }
    }, isIOS ? 5000 : 3000);

    return () => clearTimeout(timer);
  }, [canShowInstall, isInstallable, isIOS]);

  const handleInstall = async () => {
    const accepted = await promptInstall();
    if (accepted) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!canShowInstall || !showPrompt || tourActive) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-3 right-3 z-[56] animate-slide-up">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl dark:shadow-black/40 overflow-hidden border border-gray-200/80 dark:border-gray-700/60">
        <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logos/coordal-logo-white.svg" alt="" className="w-6 h-6" />
            <span className="text-white font-semibold text-sm">Coordal Maps</span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-0.5 text-white/70 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
            Get the full experience
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
            Add Coordal Maps to your Home Screen for instant access, faster loads, and a full-screen map experience — no app store needed.
          </p>

          {isIOS ? (
            <>
              <div className="space-y-2.5">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400">1</span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Tap the <Share size={13} className="inline text-blue-500 -mt-0.5" /> <span className="font-medium">Share</span> button in the toolbar
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400">2</span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Scroll down and tap <span className="font-medium">&quot;Add to Home Screen&quot;</span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400">3</span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Tap <span className="font-medium">&quot;Add&quot;</span> to confirm
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleDismiss}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  Maybe later
                </button>
                <button
                  onClick={handleDismiss}
                  className="flex-1 py-2.5 text-sm font-medium bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-xl flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity shadow-sm"
                >
                  <ArrowUp size={15} />
                  Got it
                </button>
              </div>
            </>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleDismiss}
                className="flex-1 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
              >
                Not now
              </button>
              <button
                onClick={handleInstall}
                disabled={!isInstallable}
                className="flex-1 py-2.5 text-sm font-medium bg-gradient-to-r from-emerald-500 to-cyan-500 disabled:opacity-60 text-white rounded-xl flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity shadow-sm"
              >
                <Download size={15} />
                Install App
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
